export type McpProviderKey = "jira" | "monday" | "linear" | "notion" | "github" | `custom-${string}`;

export type McpConnectorPreset = {
  key: McpProviderKey;
  title: string;
  description: string;
  base_url_placeholder: string;
  credential_label: string;
  auth_type?: "oauth" | "manual";
};

/**
 * The gateway's connector lifecycle (app/models.py ConnectorStatus):
 *   configured — saved, nothing discovered yet    connected — OAuth done, no tools found
 *   live       — OAuth done and tools discovered
 *   error      — discovery/invoke failed          expired   — token refresh failed, re-auth needed
 * Only the last two mean the agent cannot call the provider. Anything that tests for
 * "live" alone silently hides a healthy connector that happens to expose no tools.
 */
export const BROKEN_CONNECTOR_STATUSES = ["error", "expired"];

export function isConnectorUsable(status: string): boolean {
  return !BROKEN_CONNECTOR_STATUSES.includes(status);
}

export type McpConnector = {
  id: string;
  org_id: string;
  user_id?: string | null;
  provider: McpProviderKey;
  name: string;
  base_url: string;
  credential_label: string;
  secret_preview: string;
  auth_type: "oauth" | "manual";
  status: string;
  /** When true the gateway refuses this connection's write tools (app/services/tool_intent.py). */
  read_only?: boolean;
  last_error?: string | null;
  created_at: string;
  updated_at: string;
  last_discovered_at?: string | null;
  tool_count: number;
};

/** Surfaces the gateway lets start an OAuth flow (app/api/deps.py _CONNECT_SURFACES). */
export type McpConnectSurface = "tools" | "git";

export type StartMcpOAuthInput = {
  provider: McpProviderKey;
  name?: string;
  /** Defaults to "tools". The Git section connects the same GitHub connector. */
  surface?: McpConnectSurface;
};

export type McpGatewayTool = {
  id: string;
  connector_id: string;
  org_id: string;
  user_id?: string | null;
  provider?: McpProviderKey | null;
  connector_name?: string | null;
  name: string;
  description: string;
  schema: Record<string, unknown>;
  discovered_at: string;
};

export type McpToolInvokeResult = {
  tool: McpGatewayTool;
  result: unknown;
};

export type CreateMcpConnectorInput = {
  provider: McpProviderKey;
  name: string;
  base_url: string;
  credential_label: string;
  credential_secret?: string;
};

export type McpServerAuthMode = "oauth" | "bearer";

/** An org-level catalog entry (`mcp_gateway_org_servers`) — distinct from a user's own connector. */
export type McpServer = {
  id: string;
  org_id: string;
  provider: `custom-${string}`;
  name: string;
  url: string;
  auth_mode: McpServerAuthMode;
  description: string;
  created_by: string;
  created_at: string;
};

export type CreateMcpServerInput = {
  name: string;
  url: string;
  auth_mode: McpServerAuthMode;
  description?: string;
};
